import React from 'react';
import { CheckCircle2, Clock, Calendar, Home, MapPin } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';
import { toast } from 'sonner';

const QuoteForm = () => {
  const [state, handleSubmit] = useForm('mkokgarv');

  // Show success toast when form is successfully submitted
  React.useEffect(() => {
    if (state.succeeded) {
      toast.success('Quote request sent! We\'ll respond within 24 hours.');
    }
  }, [state.succeeded]);

  // Show error toast if submission fails
  React.useEffect(() => {
    if (state.errors && state.errors.length > 0) {
      toast.error('There was an error submitting your request. Please try again.');
    }
  }, [state.errors]);

  return (
    <section id="quote" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Info */}
          <div className="space-y-8">
            <div>
              <h2 className="font-['Bebas_Neue'] text-6xl md:text-7xl text-white mb-4">
                LET'S GET YOUR <span className="text-[#CC0000]">WINDOWS CLEAN</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                Fill out the form and we'll send you a free, no-obligation quote within 24 hours. 
                We make it easy to get crystal clear windows.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="text-[#CC0000] flex-shrink-0 mt-1" size={24} />
                <div>
                  <div className="text-white font-semibold text-lg">Free Estimate</div>
                  <div className="text-gray-400">No hidden fees or obligations</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Clock className="text-[#CC0000] flex-shrink-0 mt-1" size={24} />
                <div>
                  <div className="text-white font-semibold text-lg">24 Hour Response</div>
                  <div className="text-gray-400">Quick turnaround on all quotes</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Calendar className="text-[#CC0000] flex-shrink-0 mt-1" size={24} />
                <div>
                  <div className="text-white font-semibold text-lg">Flexible Scheduling</div>
                  <div className="text-gray-400">We work around your schedule</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Home className="text-[#CC0000] flex-shrink-0 mt-1" size={24} />
                <div>
                  <div className="text-white font-semibold text-lg">Residential Welcome</div>
                  <div className="text-gray-400">Serving all types of homes</div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <MapPin className="text-[#CC0000] flex-shrink-0 mt-1" size={24} />
                <div>
                  <div className="text-white font-semibold text-lg">Serving Lubbock Area</div>
                  <div className="text-gray-400">Lubbock and surrounding West Texas</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 rounded-lg p-8">
            {state.succeeded ? (
              <div className="text-center py-12">
                <div className="mb-6">
                  <CheckCircle2 className="text-[#CC0000] mx-auto" size={64} />
                </div>
                <h3 className="font-['Bebas_Neue'] text-4xl text-white mb-4">
                  Quote Request Sent!
                </h3>
                <p className="text-gray-400 text-lg mb-6">
                  We'll get back to you within 24 hours with a free estimate.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-[#CC0000] text-white px-6 py-3 rounded hover:bg-[#B30000] transition-colors duration-300 font-semibold"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                      placeholder="John"
                    />
                    <ValidationError field="firstName" errors={state.errors} className="text-red-400 text-sm mt-1" />
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                      placeholder="Doe"
                    />
                    <ValidationError field="lastName" errors={state.errors} className="text-red-400 text-sm mt-1" />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                    placeholder="john@example.com"
                  />
                  <ValidationError field="email" errors={state.errors} className="text-red-400 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                    placeholder="(806) 555-0123"
                  />
                  <ValidationError field="phone" errors={state.errors} className="text-red-400 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">
                    Property Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                    placeholder="123 Main St, Lubbock, TX"
                  />
                  <ValidationError field="address" errors={state.errors} className="text-red-400 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">
                    Property Type
                  </label>
                  <select
                    name="propertyType"
                    className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                  >
                    <option value="">Select property type</option>
                    <option value="residential-single">Residential Single Family</option>
                    <option value="residential-multi">Residential Multi-Story</option>
                    <option value="other">Other</option>
                  </select>
                  <ValidationError field="propertyType" errors={state.errors} className="text-red-400 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">
                    Service Needed
                  </label>
                  <select
                    name="serviceNeeded"
                    className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                  >
                    <option value="">Select service</option>
                    <option value="exterior">Window Cleaning Exterior</option>
                    <option value="interior-exterior">Window Cleaning Interior + Exterior</option>
                    <option value="hard-to-reach">Hard-to-Reach / Second Story</option>
                    <option value="full-package">Full Package</option>
                    <option value="not-sure">Not Sure</option>
                  </select>
                  <ValidationError field="serviceNeeded" errors={state.errors} className="text-red-400 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">
                    Number of Windows
                  </label>
                  <select
                    name="numberOfWindows"
                    className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors"
                  >
                    <option value="">Select number</option>
                    <option value="1-10">1-10 windows</option>
                    <option value="11-20">11-20 windows</option>
                    <option value="21-30">21-30 windows</option>
                    <option value="30+">30+ windows</option>
                  </select>
                  <ValidationError field="numberOfWindows" errors={state.errors} className="text-red-400 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    rows="4"
                    className="w-full px-4 py-3 bg-black border border-zinc-700 rounded text-white focus:outline-none focus:border-[#CC0000] transition-colors resize-none"
                    placeholder="Any specific requests or questions?"
                  ></textarea>
                  <ValidationError field="notes" errors={state.errors} className="text-red-400 text-sm mt-1" />
                </div>

                <button
                  type="submit"
                  disabled={state.submitting}
                  className="w-full bg-[#CC0000] text-white px-8 py-4 rounded text-lg font-semibold hover:bg-[#B30000] transition-all duration-300 shadow-lg hover:shadow-red-900/50 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {state.submitting ? 'Sending...' : 'Get Free Quote'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
